// ============================================================
// WarningDayChips.tsx — Round 2: a deadline type's "days before" warnings
// as chips. Shared by Admin › Deadline types (the firm's schedule) and
// Settings › Notifications (a person's extra reminders).
//
//   • locked  — can't be removed: the limitation floor (30 / 7 / 1) in
//               Admin, and the firm's whole schedule in Settings. Shown with
//               a lock and the reason as a tooltip.
//   • editable — × removes; the small field + Add adds a day (1–365).
// The parent owns saving: onChange(nextDays) fires with the EDITABLE days
// only (sorted, largest first) — the backend re-applies the floor anyway.
// ============================================================
import React, { useState } from 'react'

const MAX_DAY = 365

const LockGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
)

export const dayLabel = (d: number): string => (d === 1 ? '1 day' : `${d} days`)

const CHIP = 'inline-flex items-center gap-1 h-[26px] px-2.5 rounded-full text-[12px] font-semibold tabular-nums'

interface Props {
  editable: number[]
  locked?: number[]
  lockedTitle?: string // tooltip on locked chips, e.g. "Required for limitation dates"
  lockedTone?: 'floor' | 'firm' // floor = amber lock (limitation); firm = neutral "set by your firm"
  onChange: (next: number[]) => void
  disabled?: boolean
  ariaLabel: string // e.g. "Warnings for Trial date"
  addPlaceholder?: string
}

const WarningDayChips: React.FC<Props> = ({
  editable, locked = [], lockedTitle, lockedTone = 'floor', onChange, disabled, ariaLabel, addPlaceholder = 'Days',
}) => {
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const lockedSet = new Set(locked)
  const all = Array.from(new Set([...locked, ...editable])).sort((a, b) => b - a)

  const add = (): void => {
    const n = Number(draft)
    if (!Number.isInteger(n) || n < 1 || n > MAX_DAY) { setError(`Enter a whole number of days from 1 to ${MAX_DAY}.`); return }
    if (all.includes(n)) { setError(`${dayLabel(n)} is already there.`); return }
    setError(null)
    setDraft('')
    onChange([...editable, n].sort((a, b) => b - a))
  }

  return (
    <div>
      <ul className="flex flex-wrap items-center gap-1.5" aria-label={ariaLabel}>
        {all.map((d) => lockedSet.has(d) ? (
          <li key={d} title={lockedTitle}
            className={`${CHIP} ${lockedTone === 'floor'
              ? 'bg-[#fbf1dc] text-[#7a5d10] border border-[#efdcae] dark:bg-[rgba(224,182,74,0.14)] dark:text-[#e0b64a] dark:border-[rgba(224,182,74,0.3)]'
              : 'bg-casevo-input text-casevo-text-soft border border-casevo-border-soft'}`}>
            <LockGlyph />{dayLabel(d)}
            <span className="sr-only"> — {lockedTitle ?? 'locked'}</span>
          </li>
        ) : (
          <li key={d} className={`${CHIP} pr-1 bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] border border-casevo-border-soft`}>
            {dayLabel(d)}
            <button type="button" disabled={disabled} onClick={() => onChange(editable.filter((x) => x !== d))}
              aria-label={`Remove the ${dayLabel(d)} warning`}
              className="ml-0.5 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full hover:bg-black/10 dark:hover:bg-white/10 focus:outline-none casevo-focus disabled:opacity-50">
              <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
            </button>
          </li>
        ))}
        <li className="inline-flex items-center gap-1">
          <input type="number" inputMode="numeric" min={1} max={MAX_DAY} value={draft} disabled={disabled}
            onChange={(e) => { setDraft(e.target.value); setError(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
            placeholder={addPlaceholder} aria-label={`Add a warning (days before) — ${ariaLabel}`}
            className="w-[70px] h-[26px] px-2 rounded-full border border-casevo-border bg-casevo-input text-[12px] text-casevo-forest tabular-nums focus:outline-none focus:border-casevo-mid disabled:opacity-50" />
          <button type="button" onClick={add} disabled={disabled || !draft.trim()}
            className="h-[26px] px-2.5 rounded-full border border-casevo-border text-[12px] font-semibold text-casevo-brand hover:border-casevo-mid focus:outline-none casevo-focus disabled:opacity-50">
            Add
          </button>
        </li>
      </ul>
      {error && <p role="alert" className="mt-1 text-[12px] text-casevo-danger">{error}</p>}
    </div>
  )
}

export default WarningDayChips
